import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toggleLayerOnOff, setActiveLayerName, updateLayerColor, updateLayerProperty } from "../../../redux/layerSlice";
import ColorSelect from "../../common/ColorSelect/ColorSelect";
import LayerSettingsDialog from "./LayerSettingsDialog";
import { getIcon } from "../../../utils/icons";
import API_URL from "../../../config";
import { colorForRoleLabel, resolveStakeholderColor } from "../../../utils/stakeholderColors";
import {
  getHiddenAuthors, setHiddenAuthors, toggleAuthorHidden,
  authorCanonicalKey, subscribeAuthorFilter,
} from "../../../utils/noteAuthorFilter";
import './LayerMenu.css';

// Global ref'ler — useSelection tarafindan kullanilir
export const layerOffModeRef = { current: false };
export const layerOffSessionRef = { current: [] }; // Bu oturumda kapatilan katmanlar

// Transparency modu ref'leri
export const transparencyModeRef = { current: false };
export const transparencyValueRef = { current: 0 };
export const transparencySessionRef = { current: [] }; // { layerName, oldValue }[]

const ICONS = {
  on: "💡",
  off: "💤",
  freeze: "❄️",
  noneFreeze: "",
};

function getLayerProps(layer) {
  const name = layer.name || layer.layerName || "";
  const color = layer.color || layer.trueColor || layer.rgb || layer.acadColor || 7;

  let status = "on";
  if (layer.onOff !== undefined) {
    status = layer.onOff ? "on" : "off";
  } else if (layer.visible !== undefined) {
    status = layer.visible ? "on" : "off";
  }

  const lock = layer.locked === true;
  const freeze = layer.frozen === true;

  return { name, color, status, lock, freeze };
}

export default function LayerMenu() {
  const [isOpen, setIsOpen] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const layers = useSelector(state => state.layer.allLayers || []);
  const currentLayer = useSelector(state => state.layer.activeLayerName);
  const dispatch = useDispatch();

  useEffect(() => {
    if (layers.length > 0 && !currentLayer) {
      const firstName = getLayerProps(layers[0]).name;
      dispatch(setActiveLayerName(firstName));
    }
  }, [layers, currentLayer, dispatch]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleOutsideClick = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target) &&
          !e.target.closest('.layer-select-dropdown')) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [dropdownOpen]);

  const handleSelect = (name) => {
    dispatch(setActiveLayerName(name));
    setDropdownOpen(false);
  };

  const handleToggleOnOff = (e, layerName) => {
    e.stopPropagation();
    dispatch(toggleLayerOnOff(layerName));
  };

  const [colorSelectOpen, setColorSelectOpen] = useState(false);
  const [colorSelectLayer, setColorSelectLayer] = useState(null);
  const triggerRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const handleColorClick = (e, layerName) => {
    e.stopPropagation();
    setColorSelectLayer(layerName);
    setColorSelectOpen(true);
  };

  const handleColorSelect = (aci, hex) => {
    if (colorSelectLayer) {
      dispatch(updateLayerColor({ layerName: colorSelectLayer, color: hex }));
    }
  };

  const [layerSettingsOpen, setLayerSettingsOpen] = useState(false);
  const [transparencyVal, setTransparencyVal] = useState(0);
  const [transparencyMode, setTransparencyMode] = useState(false);
  const [layerOffMode, setLayerOffMode] = useState(false);

  // ─── Paydaş notu filtresi (sahnede hangi paydaş notları görünsün) ─
  const projectId = useSelector(state => state.project?.projectId);
  const shareToken = useSelector(state => state.project?.shareAccess?.token);
  const [stkOpen, setStkOpen] = useState(false);
  const [stkAuthors, setStkAuthors] = useState([]);   // { key, name, role, count, color }[]
  const [stkHidden, setStkHidden] = useState(getHiddenAuthors());
  const [stkPopPos, setStkPopPos] = useState({ top: 0, right: 0 });
  const stkBtnRef = useRef(null);
  const stkPopRef = useRef(null);

  // Subscribe — store değiştiğinde re-render
  useEffect(() => subscribeAuthorFilter((set) => setStkHidden(new Set(set))), []);

  // Popover açıldığında trigger butonunun konumuna göre fixed pozisyon hesapla
  useEffect(() => {
    if (!stkOpen) return;
    const calc = () => {
      const r = stkBtnRef.current?.getBoundingClientRect();
      if (!r) return;
      setStkPopPos({
        top: r.bottom + 6,
        right: window.innerWidth - r.right,
      });
    };
    calc();
    window.addEventListener('resize', calc);
    window.addEventListener('scroll', calc, true);
    return () => {
      window.removeEventListener('resize', calc);
      window.removeEventListener('scroll', calc, true);
    };
  }, [stkOpen]);

  // Proje notlarını çek + yazarları derive et (5sn polling)
  useEffect(() => {
    if (!projectId) { setStkAuthors([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('projabze_token');
        const url = `${API_URL}/api/projects/${projectId}/notes${shareToken ? `?shareToken=${shareToken}` : ''}`;
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const { notes } = await res.json();
        if (cancelled) return;
        const map = new Map();
        for (const n of (notes || [])) {
          const k = authorCanonicalKey({ authorUserId: n.authorUserId, authorName: n.authorName });
          if (!map.has(k)) {
            map.set(k, {
              key: k,
              name: n.authorName || 'Anonim',
              role: n.authorRole || null,
              count: 0,
              color: colorForRoleLabel(n.authorRole) || resolveStakeholderColor({ role: n.authorRole, fallbackSeed: n.authorName }),
            });
          }
          map.get(k).count++;
        }
        setStkAuthors([...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'tr')));
      } catch (_) { /* ignore */ }
    };
    load();
    const i = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(i); };
  }, [projectId, shareToken]);

  // Outside click → kapat
  useEffect(() => {
    if (!stkOpen) return;
    const onDoc = (e) => {
      if (stkBtnRef.current && !stkBtnRef.current.contains(e.target)
          && stkPopRef.current && !stkPopRef.current.contains(e.target)) {
        setStkOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [stkOpen]);

  const stkAllOn = () => setHiddenAuthors([]);
  const stkAllOff = () => setHiddenAuthors(stkAuthors.map(a => a.key));
  const stkAnyHidden = stkHidden.size > 0;

  const cleanupMode = useCallback(() => {
    setLayerOffMode(false);
    layerOffModeRef.current = false;
    layerOffSessionRef.current = [];
    document.body.classList.remove('layer-off-cursor');
  }, []);

  // ESC: geri al + cik | Enter: onayla + cik
  const cancelLayerOff = useCallback(() => {
    // Oturumda kapatilan katmanlari geri ac
    layerOffSessionRef.current.forEach((name) => {
      dispatch(toggleLayerOnOff(name));
    });
    cleanupMode();
  }, [dispatch, cleanupMode]);

  const confirmLayerOff = useCallback(() => {
    cleanupMode();
  }, [cleanupMode]);

  const handleLayerOff = () => {
    if (layerOffMode) {
      confirmLayerOff();
    } else {
      setLayerOffMode(true);
      layerOffModeRef.current = true;
      layerOffSessionRef.current = [];
      document.body.classList.add('layer-off-cursor');
    }
  };

  useEffect(() => {
    if (!layerOffMode) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        cancelLayerOff();
      } else if (e.key === 'Enter') {
        confirmLayerOff();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [layerOffMode, cancelLayerOff, confirmLayerOff]);

  const selectedLayerRaw = layers.find(l => getLayerProps(l).name === currentLayer);
  const selectedLayer = selectedLayerRaw ? getLayerProps(selectedLayerRaw) : null;

  // Transparency mode cleanup
  const cleanupTransparencyMode = useCallback(() => {
    setTransparencyMode(false);
    transparencyModeRef.current = false;
    transparencySessionRef.current = [];
    document.body.classList.remove('layer-off-cursor');
  }, []);

  const cancelTransparency = useCallback(() => {
    // Oturumda degistirilen katmanlarin eski degerlerini geri yukle
    transparencySessionRef.current.forEach(({ layerName, oldValue }) => {
      dispatch(updateLayerProperty({
        layerName,
        property: 'transparency',
        value: oldValue,
      }));
    });
    cleanupTransparencyMode();
  }, [dispatch, cleanupTransparencyMode]);

  const confirmTransparency = useCallback(() => {
    cleanupTransparencyMode();
  }, [cleanupTransparencyMode]);

  const handleTransparencyToggle = () => {
    if (transparencyMode) {
      confirmTransparency();
    } else {
      setTransparencyMode(true);
      transparencyModeRef.current = true;
      transparencyValueRef.current = transparencyVal;
      transparencySessionRef.current = [];
      document.body.classList.add('layer-off-cursor');
    }
  };

  // transparencyVal degistiginde ref'i guncelle (mode aktifken select degisirse)
  useEffect(() => {
    transparencyValueRef.current = transparencyVal;
  }, [transparencyVal]);

  // ESC/Enter: transparency mode
  useEffect(() => {
    if (!transparencyMode) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        cancelTransparency();
      } else if (e.key === 'Enter') {
        confirmTransparency();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [transparencyMode, cancelTransparency, confirmTransparency]);

  const renderLayerRow = (layer, isClickable) => (
    <>
      <span
        title={layer.status==='on'?'Açık - Kapatmak için tıkla':'Kapalı - Açmak için tıkla'}
        style={{marginRight:4, color: '#ffe066', opacity: layer.status==='on' ? 1 : 0.4, cursor: 'pointer'}}
        onClick={isClickable ? (e) => handleToggleOnOff(e, layer.name) : undefined}
      >{ICONS.on}</span>
      <span title={layer.freeze?'Freeze':'Aktif'} style={{marginRight:4, color: layer.freeze ? '#00eaff' : '#888', filter: layer.freeze ? 'none' : 'grayscale(1) opacity(0.6)'}}>{ICONS.freeze}</span>
      <span title={layer.lock ? "Kilitli" : "Açık"} style={{marginRight:4, display:'inline-flex', width:18, height:18}}>
        {getIcon(layer.lock ? "locked" : "unlocked", 18)}
      </span>
      <span
        style={{display:'inline-block',width:16,height:16,borderRadius:0,marginRight:6,cursor:'pointer',border:'1px solid #555',background:typeof layer.color==='string'&&layer.color.startsWith('#')?layer.color:`#${(typeof layer.color==='number'?layer.color.toString(16).padStart(6,'0'):'ffffff')}`}}
        title={`Renk: ${layer.color} — degistirmek icin tikla`}
        onClick={isClickable ? (e) => handleColorClick(e, layer.name) : undefined}
      ></span>
      <span style={{fontWeight:500, fontSize:'12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex:1}}>{layer.name}</span>
    </>
  );

  return (
    <div className={`layerMenu ${isOpen ? "open" : "closed"}`} style={{ position: 'relative' }}>
      {isOpen && (
        <div className="layerMenu-left">
          {/* Select box */}
          <div className="layer-select-trigger" ref={triggerRef} onClick={() => {
            if (!dropdownOpen && triggerRef.current) {
              const rect = triggerRef.current.getBoundingClientRect();
              setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
            }
            setDropdownOpen(d => !d);
          }}>
            {selectedLayer ? renderLayerRow(selectedLayer, false) : <span>Katman Sec</span>}
            <span style={{marginLeft:'auto', paddingLeft:4, flexShrink:0}}>&#9662;</span>
          </div>
          {dropdownOpen && (
            <div className="layer-select-dropdown" style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}>
              {layers.map(layerRaw => {
                const layer = getLayerProps(layerRaw);
                return (
                  <div
                    key={layer.name}
                    className={`layer-select-option${currentLayer === layer.name ? ' selected' : ''}`}
                    onClick={() => handleSelect(layer.name)}
                  >
                    {renderLayerRow(layer, true)}
                  </div>
                );
              })}
            </div>
          )}

          {/* Layer toolbar */}
          <div className="layer-toolbar">
            <button
              className={`layer-tool-btn${layerOffMode ? ' active-mode' : ''}`}
              onClick={handleLayerOff}
              title={layerOffMode ? 'Enter: Onayla | ESC: Geri al' : 'Layer Off — nesneye tiklayarak katman kapat'}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="14" r="5" stroke={layerOffMode ? '#ff4444' : '#ffe066'} />
                <line x1="12" y1="9" x2="12" y2="4" stroke={layerOffMode ? '#ff4444' : '#ffe066'} />
                <line x1="4" y1="4" x2="20" y2="20" stroke={layerOffMode ? '#ff4444' : '#ffe066'} strokeWidth="2.5" />
              </svg>
            </button>
            <button
              className="layer-tool-btn"
              onClick={() => setLayerSettingsOpen(true)}
              title="Katman Ayarlari"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="4" rx="1" stroke="#ffe066" fill="rgba(255,224,102,0.15)" />
                <rect x="3" y="10" width="18" height="4" rx="1" stroke="#00eaff" fill="rgba(0,234,255,0.15)" />
                <rect x="3" y="16" width="18" height="4" rx="1" stroke="#ff6b6b" fill="rgba(255,107,107,0.15)" />
              </svg>
            </button>
            <div className={`layer-transparency-group${transparencyMode ? ' active-mode' : ''}`}>
              <button
                className="layer-tool-btn layer-transparency-btn"
                onClick={handleTransparencyToggle}
                title={transparencyMode ? 'Enter: Onayla | ESC: Geri al' : 'Transparency — nesneye tiklayarak seffaflik uygula'}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="4" width="16" height="16" rx="2" stroke={transparencyMode ? '#ff4444' : '#aaa'} strokeDasharray="3 2" />
                  <rect x="4" y="4" width="16" height="16" rx="2" fill={transparencyMode ? 'rgba(255,68,68,0.15)' : 'rgba(255,255,255,0.15)'} />
                  <line x1="4" y1="20" x2="20" y2="4" stroke={transparencyMode ? '#ff4444' : '#aaa'} strokeWidth="1" />
                </svg>
              </button>
              <select
                className="layer-transparency-select"
                value={transparencyVal}
                onChange={(e) => setTransparencyVal(parseInt(e.target.value))}
                title="Seffaflik degeri"
              >
                {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90].map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            {/* Paydaş notu filtresi */}
            <div className="layer-stk-filter" style={{ position: 'relative' }}>
              <button
                ref={stkBtnRef}
                className={`layer-tool-btn layer-stk-btn${stkAnyHidden ? ' has-hidden' : ''}`}
                onClick={() => setStkOpen(o => !o)}
                title={stkAnyHidden
                  ? `${stkHidden.size} paydas notu gizli`
                  : 'Paydas notlari — gosterilecek paydaslari sec'}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span className="layer-stk-label">Paydas Notlari</span>
                {stkAnyHidden && <span className="layer-stk-badge">{stkHidden.size}</span>}
                <span className="layer-stk-caret">▼</span>
              </button>
              {stkOpen && (
                <div
                  ref={stkPopRef}
                  className="layer-stk-popover"
                  style={{ top: stkPopPos.top, right: stkPopPos.right }}
                >
                  <div className="layer-stk-pop-head">
                    <span>Paydas notlari</span>
                    <div className="layer-stk-pop-actions">
                      <button onClick={stkAllOn}>Hepsi</button>
                      <button onClick={stkAllOff}>Hicbiri</button>
                    </div>
                  </div>
                  {stkAuthors.length === 0 ? (
                    <div className="layer-stk-empty">Henuz not yok</div>
                  ) : (
                    <div className="layer-stk-list">
                      {stkAuthors.map(a => {
                        const hidden = stkHidden.has(a.key);
                        const initials = a.name.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
                        return (
                          <label key={a.key} className={`layer-stk-item${hidden ? ' off' : ''}`}>
                            <input
                              type="checkbox"
                              checked={!hidden}
                              onChange={() => toggleAuthorHidden(a.key)}
                            />
                            <span className="layer-stk-color" style={{ background: a.color }}>
                              {initials || '?'}
                            </span>
                            <div className="layer-stk-info">
                              <div className="layer-stk-name">{a.name}</div>
                              {a.role && <div className="layer-stk-role">{a.role}</div>}
                            </div>
                            <span className="layer-stk-count">{a.count}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="layerMenu-toggle">
        <button
          className="toolButton toggleArrowButton"
          onClick={() => setIsOpen(prev => !prev)}
        >
          {isOpen ? "\u25C0" : "Katmanlar\u25B6"}
        </button>
      </div>
      <ColorSelect
        isOpen={colorSelectOpen}
        onClose={() => setColorSelectOpen(false)}
        onSelect={handleColorSelect}
        initialAci={7}
      />
      <LayerSettingsDialog
        isOpen={layerSettingsOpen}
        onClose={() => setLayerSettingsOpen(false)}
      />
    </div>
  );
}
